export const financingPageData = {
  stats: [
    { value: "60%", label: "Higher Average Order Value" },
    { value: "40%", label: "Better Customer Retention" },
    { value: "5 min", label: "Credit Approval Time" },
    { value: "3×", label: "New Buyer Acquisition" },
  ],
  statsAccent: "from-emerald-600 to-green-600",

  challengeTitle: "Cash Flow Constraints Are the #1 Barrier to B2B Growth",
  challengeSubtitle:
    "Your customers want to buy more. Rigid payment terms and lack of financing options are stopping them.",
  challenges: [
    {
      icon: "💰",
      title: "Customers Can't Buy What They Want",
      description:
        "Limited cash flow forces buyers to place smaller orders than they need — capping your revenue potential.",
    },
    {
      icon: "🏦",
      title: "Traditional Financing Is Too Slow",
      description:
        "Bank financing takes weeks to approve. By the time the customer has credit, the opportunity has passed.",
    },
    {
      icon: "📉",
      title: "Losing Deals to Flexible Competitors",
      description:
        "Competitors offering BNPL or extended terms win deals you should be closing — on price terms, not product quality.",
    },
    {
      icon: "🔄",
      title: "Low Repeat Purchase Frequency",
      description:
        "Without loyalty incentives tied to payment, customers don't have a compelling reason to return faster.",
    },
    {
      icon: "📊",
      title: "No Credit Risk Visibility",
      description:
        "Extending credit without data-driven risk assessment leads to bad debt and cash flow problems.",
    },
    {
      icon: "🎯",
      title: "One-Size-Fits-All Terms",
      description:
        "Static payment terms ignore each customer's financial situation and purchasing potential — leaving money on the table.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
    solutionIconBg: "bg-emerald-50",
    solutionIconColor: "text-emerald-600",
    solutionAccent: "from-emerald-600 to-green-600",
  },

  solutionTitle: "Embed Flexible Financing Directly Into Your Sales Workflow",
  solutionSubtitle:
    "Remove the biggest barrier to larger orders by offering seamless B2B financing — approved in minutes, not weeks.",
  solutions: [
    {
      icon: "🛒",
      title: "B2B Buy Now, Pay Later",
      description:
        "Let customers buy what they need today and pay over time — in installments that fit their cash flow.",
    },
    {
      icon: "📋",
      title: "Invoice Financing",
      description:
        "Customers unlock credit against their outstanding invoices, freeing working capital without traditional bank loans.",
    },
    {
      icon: "💳",
      title: "Revolving Credit Lines",
      description:
        "Pre-approved credit lines let repeat buyers order instantly — with automatic repayment aligned to payment cycles.",
    },
    {
      icon: "⚡",
      title: "5-Minute AI Credit Assessment",
      description:
        "AI analyses order history, payment behaviour, and business signals to approve credit in under 5 minutes — not weeks.",
    },
    {
      icon: "🏆",
      title: "AI-Driven Loyalty Rewards",
      description:
        "Personalised discounts, cashback, and loyalty rewards automatically triggered at the optimal moment in each buyer's cycle.",
    },
    {
      icon: "🔒",
      title: "Built-In Risk Management",
      description:
        "Real-time credit exposure monitoring with automatic limits and alerts protects you from bad debt before it happens.",
    },
    {
      icon: "🔗",
      title: "Embedded at Point of Purchase",
      description:
        "Financing options appear seamlessly during checkout — no redirects, no paperwork, no friction.",
    },
    {
      icon: "📊",
      title: "Financing Analytics Dashboard",
      description:
        "Monitor credit utilisation, repayment performance, and financing's impact on order values across your entire customer base.",
    },
    {
      icon: "🌍",
      title: "Multi-Currency Support",
      description:
        "Deploy financing across international markets with multi-currency support and localised payment terms.",
    },
  ],

  howItWorksTitle: "Financing Live in Your Sales Flow in 3 Steps",
  howItWorksSteps: [
    {
      icon: "⚙️",
      title: "Configure Your Offer",
      description: "Set financing terms, credit limits, and eligible products in your EkkBaz AI dashboard.",
    },
    {
      icon: "🛒",
      title: "Customer Checks Out",
      description: "Financing options appear at checkout. Customer selects preferred terms and AI approves in minutes.",
    },
    {
      icon: "📦",
      title: "Order Ships Immediately",
      description: "You receive payment upfront from EkkBaz. Customer repays on their agreed schedule.",
    },
    {
      icon: "📈",
      title: "Orders Grow Consistently",
      description: "Track the direct impact on average order value, retention, and new customer acquisition.",
    },
  ],

  outcomeTitle: "Financing That Transforms Your Revenue Metrics",
  outcomeSubtitle: "Real outcomes from businesses with EkkBaz AI Embedded Financing.",
  outcomes: [
    {
      value: "60%",
      metric: "Higher Average Order Value",
      description:
        "Customers empowered with financing buy significantly more per order — directly growing your top-line revenue.",
    },
    {
      value: "40%",
      metric: "Improved Customer Retention",
      description:
        "Loyalty rewards and flexible payment terms create strong recurring purchase behaviour and dramatically reduce churn.",
    },
    {
      value: "3×",
      metric: "Faster New Buyer Acquisition",
      description:
        "Flexible financing terms attract new customers who previously couldn't afford to buy at your terms.",
    },
  ],
  outcomeImage: "/assets/images/products/financing2.png",

  ctaTitle: "Give Your Customers the Buying Power to Grow Your Revenue",
  ctaSubtitle:
    "Book a consultation and discover how EkkBaz AI Embedded Financing can increase your average order value by 60% — this quarter.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const financingData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Cash Flow Constraints Are Limiting Your Customers' Purchasing Power",
    description:
      "Your B2B customers want to buy more, but tight cash flow and rigid payment terms hold them back. Meanwhile, your business is leaving revenue on the table — and competitors who offer flexible financing are winning deals you should be closing.",
    banner: "/assets/images/products/financing1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Embed Flexible B2B Financing Directly into Your Sales Workflow",
    description:
      "EkkBaz AI's Embedded Financing platform lets you offer your customers a range of payment solutions — including Buy Now Pay Later (BNPL) for B2B, invoice financing, and revolving credit lines — directly at the point of purchase. AI assesses buyer creditworthiness in real time and approves financing within minutes, creating a seamless experience that removes the biggest barrier to larger orders.",
    banner: "/assets/images/products/financing2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Increase Average Order Value 60% with AI-Driven Loyalty and Financing",
    description:
      "Businesses using EkkBaz Embedded Financing see average order values increase by 60%, customer retention improve by 40%, and new buyer acquisition accelerate as flexible payment terms become a key competitive differentiator. AI-driven loyalty rewards further strengthen customer relationships and drive repeat purchasing.",
    banner: "/assets/images/products/financing3.png",
  },
];

export const financingFAQs = [
  {
    question: "What types of B2B financing does EkkBaz AI offer?",
    answer:
      "EkkBaz AI supports multiple B2B financing models including Buy Now Pay Later (BNPL), invoice financing, revolving credit lines, and installment payment plans — all embedded directly into the purchasing workflow for a seamless buyer experience.",
  },
  {
    question: "How does EkkBaz AI assess buyer creditworthiness?",
    answer:
      "EkkBaz AI analyzes order history, payment behavior, business profile, and real-time financial signals to generate instant credit assessments — enabling financing approvals in minutes rather than days, without requiring traditional bank documentation.",
  },
  {
    question: "How do AI-driven loyalty rewards work in EkkBaz?",
    answer:
      "EkkBaz AI analyzes each customer's purchasing patterns and automatically generates personalized discounts, cashback offers, and loyalty rewards at optimal moments in the buying cycle to maximize repeat purchasing and customer lifetime value.",
  },
];
